// zod mirrors of contracts/admin-api.md. Every response is parsed before use.
//
// Puzzle specs are validated only shallowly (envelope + component kinds): the
// renderer must cope with components newer than this build, so an unknown glyph
// must not fail a whole response.

import { z } from "zod";
import type { PuzzleSpec } from "@lumen/puzzle-render";
import { normaliseEmblem, type Emblem } from "@lumen/team-emblem";
import { isoWeekOf, recentIsoWeeks, todayUtc } from "../lib/dates";
import { LIFETIME_PRODUCT, planOf } from "../lib/pro";

export const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");
const id = z.union([z.string(), z.number()]).transform(String);
const count = z.number().int().nonnegative();
const rate = z.number().min(0).max(1);

// ---------------------------------------------------------------- shared enums

export const Role = z.enum(["viewer", "editor", "admin"]);
export type Role = z.infer<typeof Role>;

export const PuzzleStatus = z.enum(["draft", "approved", "published", "rejected", "retired"]);
export type PuzzleStatus = z.infer<typeof PuzzleStatus>;

export const Category = z.enum(["pattern", "number", "spatial", "logic", "memory", "speed", "planning", "word"]);
export type Category = z.infer<typeof Category>;

export const Track = z.enum(["pattern", "number", "spatial", "logic", "memory", "speed", "planning", "word", "mixed"]);
export type Track = z.infer<typeof Track>;

export const IntegrityFlag = z.enum(["too_fast", "clock_mismatch"]);

/** Puzzle bank sort keys; `-` is descending, undated puzzles sort last. */
export const PuzzleSort = z.enum(["date", "-date", "id", "-id", "difficulty", "-difficulty"]);
export type PuzzleSort = z.infer<typeof PuzzleSort>;
export type IntegrityFlag = z.infer<typeof IntegrityFlag>;

// ---------------------------------------------------------------- puzzle spec (shallow)

const Kinded = z.looseObject({ kind: z.string() });

export const PuzzleSpecSchema = z
  .looseObject({
    spec: z.literal(1),
    id: z.string(),
    type: z.string(),
    typeVersion: z.number().int().optional(),
    category: z.string(),
    requires: z.array(z.string()),
    difficulty: z.looseObject({ score: z.number().min(0).max(1), tier: z.number().int().min(1).max(5).optional() }),
    timeBudgetMs: z.number().int(),
    prompt: z.looseObject({ text: z.string(), detail: z.string().optional() }),
    palette: z.string().optional(),
    board: Kinded,
    phases: z.array(Kinded).optional(),
    answer: Kinded,
    accessibility: z.looseObject({}).optional(),
    check: z.object({ alg: z.literal("sha256"), digest: z.string().regex(/^[0-9a-f]{64}$/) }).optional(),
  })
  .transform((s) => s as unknown as PuzzleSpec);

const CellTuple = z.tuple([z.number().int().nonnegative(), z.number().int().nonnegative()]);
/** Launch kinds store their value in canonical form (contracts/README.md "Answers"); an int is accepted for a number. */
const IntText = z.union([z.string(), z.number().int()]).transform((v) => String(v).trim());

export const AnswerSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("choice"), value: z.string() }),
  z.object({ kind: z.literal("cellSet"), value: z.array(CellTuple) }),
  // Launch types (contracts/launch-types.md §1).
  z.object({ kind: z.literal("number"), value: IntText }),
  z.object({ kind: z.literal("digits"), value: z.string() }),
  z.object({ kind: z.literal("fill"), value: z.array(z.union([z.number().int(), z.string().transform((v) => Number(v.trim()))])) }),
  z.object({ kind: z.literal("sequence"), value: z.array(CellTuple) }),
  z.object({ kind: z.literal("rows"), value: z.string().transform((v) => v.trim().toUpperCase()) }),
]);
export type AnswerValue = z.infer<typeof AnswerSchema>;

export const Difficulty = z.object({ score: z.number().min(0).max(1), tier: z.number().int().min(1).max(5).optional() });

// ---------------------------------------------------------------- errors

export const ErrorBody = z.object({ error: z.object({ code: z.string(), message: z.string() }) });

// ---------------------------------------------------------------- auth

export const Admin = z.object({ id, email: z.string(), role: Role });
export type Admin = z.infer<typeof Admin>;
export const AdminResponse = z.object({ admin: Admin });

// ---------------------------------------------------------------- dashboard

export const Dashboard = z.object({
  date: isoDate,
  puzzleNumber: z.number().int().nullable(),
  puzzleId: z.string().nullable(),
  type: z.string().nullable(),
  players: z.object({ total: count, newToday: count }),
  daily: z.object({
    started: count,
    submitted: count,
    correctRate: rate.nullable(),
    medianElapsedMs: z.number().nonnegative().nullable(),
    flagged: count,
    bins: z.array(count).length(50),
  }),
  levels: z.object({ completionsToday: count, activePlayers: count }),
  streaks: z.object({ active: count, longest: count }),
  last7: z.array(z.object({ date: isoDate, submitted: count, correctRate: rate.nullable() })),
  /** Phase 1 (contracts/phase1.md "Admin additions"); optional so a Phase 0 backend still parses. */
  shares: z.object({ today: count, byChannel: z.record(z.string(), count) }).optional(),
  devices: z.object({ withPush: count }).optional(),
});
export type Dashboard = z.infer<typeof Dashboard>;

/** POST /v1/daily/{n}/share channels, in display order. */
export const SHARE_CHANNELS = ["copy", "messages", "whatsapp", "instagram", "tiktok", "x", "other"] as const;

// ---------------------------------------------------------------- puzzles

// Puzzle Studio (contracts/studio.md "Implementation notes"): the undated bank, where a puzzle came
// from, its review state and its latest verification. Optional on the Phase 0 shapes so older
// backends (and the daily mock) still parse; the Studio's own bank schemas require them.
export const PuzzleRowMode = z.enum(["daily", "hard", "bank"]);
export type PuzzleRowMode = z.infer<typeof PuzzleRowMode>;
export const PuzzleSource = z.enum(["generator", "curated", "ai", "manual"]);
export type PuzzleSource = z.infer<typeof PuzzleSource>;
export const ReviewState = z.enum(["none", "pending", "approved", "rejected"]);
export type ReviewState = z.infer<typeof ReviewState>;
/** The list's `{method, ok}`; null for generator rows (verified by the content build). */
export const VerificationSummary = z.object({ method: z.enum(["solver", "manual"]), ok: z.boolean() }).nullable();
export type VerificationSummary = z.infer<typeof VerificationSummary>;
/** The engine's (or the curated file's) full verification. */
export const VerificationResult = z.looseObject({
  method: z.enum(["solver", "manual"]),
  ok: z.boolean(),
  unique: z.boolean().nullable().optional(),
  solverAnswers: z.array(z.unknown()).optional(),
  difficultyScore: z.number().nullable().optional(),
  issues: z.array(z.string()).optional(),
  warnings: z.array(z.string()).optional(),
  notes: z.string().optional(),
  by: z.enum(["engine", "curated"]).optional(),
  at: z.string().optional(),
});
export type VerificationResult = z.infer<typeof VerificationResult>;

export const PuzzleListItem = z.object({
  id: z.string(),
  puzzleNumber: z.number().int().nullable(),
  date: isoDate.nullable(),
  type: z.string(),
  category: Category,
  difficulty: Difficulty,
  status: PuzzleStatus,
  // Studio (optional so a pre-Studio backend still parses)
  mode: PuzzleRowMode.optional(),
  source: PuzzleSource.optional(),
  reviewRequired: z.boolean().optional(),
  reviewState: ReviewState.optional(),
  verification: VerificationSummary.optional(),
});
export type PuzzleListItem = z.infer<typeof PuzzleListItem>;

export const PuzzleList = z.object({ items: z.array(PuzzleListItem), nextCursor: z.string().nullable() });
export type PuzzleList = z.infer<typeof PuzzleList>;

/** One status transition; `by` is the admin's email, null for system changes (auto-publish). */
export const StatusChange = z.object({
  from: PuzzleStatus.nullable(),
  status: PuzzleStatus,
  at: z.string(),
  by: z.string().nullable(),
});
export type StatusChange = z.infer<typeof StatusChange>;

export const PuzzleDetail = z.object({
  puzzle: z.object({
    id: z.string(),
    puzzleNumber: z.number().int().nullable(),
    date: isoDate.nullable(),
    type: z.string(),
    category: Category,
    status: PuzzleStatus,
    spec: PuzzleSpecSchema,
    answer: AnswerSchema,
    explanation: z.string().nullable(),
    /** Email of the approving admin. */
    approvedBy: z.string().nullable(),
    approvedAt: z.string().nullable(),
    /** Status transitions, oldest first. */
    history: z.array(StatusChange),
    // Studio (optional so a pre-Studio backend still parses)
    mode: PuzzleRowMode.optional(),
    source: PuzzleSource.optional(),
    /** Curated: the entry's sourceId, e.g. `pattern-24`. */
    sourceId: z.string().nullable().optional(),
    reviewRequired: z.boolean().optional(),
    reviewState: ReviewState.optional(),
    verification: VerificationResult.nullable().optional(),
  }),
  stats: z.object({
    submitted: count,
    correctRate: rate.nullable(),
    avgScore: z.number().nullable(),
    medianElapsedMs: z.number().nullable(),
    optionPickRate: z.record(z.string(), rate),
  }),
});
export type PuzzleDetail = z.infer<typeof PuzzleDetail>;

export const PatchPuzzleResponse = z.object({
  puzzle: z.looseObject({ id: z.string(), status: PuzzleStatus }),
});

// ---------------------------------------------------------------- schedule

export const ScheduleDay = z.object({
  date: isoDate,
  puzzleNumber: z.number().int(),
  puzzleId: z.string().nullable(),
  type: z.string().nullable(),
  category: Category.nullable(),
  tier: z.number().int().min(1).max(5).nullable(),
  status: PuzzleStatus.nullable(),
});
export type ScheduleDay = z.infer<typeof ScheduleDay>;

export const Schedule = z.object({ days: z.array(ScheduleDay) });
export type Schedule = z.infer<typeof Schedule>;

export const SwapResponse = z.object({ days: z.array(ScheduleDay) });
export const ApproveRangeResponse = z.object({ approved: count });

// ---------------------------------------------------------------- levels

export const LevelFlag = z.enum(["intro", "relief", "gate", "chapterEnd"]);
export type LevelFlag = z.infer<typeof LevelFlag>;

/** content: seeded from content/out (pnpm seed) · studio: published from the admin (contracts/studio.md §5). */
export const PackSource = z.enum(["content", "studio"]);
export type PackSource = z.infer<typeof PackSource>;

/** A Studio draft pack waiting for review (never served to players). */
export const DraftSummary = z.object({
  index: z.number().int().nonnegative(),
  first: z.number().int(),
  last: z.number().int(),
  chapter: z.string(),
  levelCount: count,
  sha256: z.string(),
  createdAt: z.string(),
  jobId: z.string().nullable(),
  verified: z.object({ checked: count, ok: count }).nullable(),
  /** A pack with this index was published meanwhile: the draft can only be deleted. */
  stale: z.boolean(),
});
export type DraftSummary = z.infer<typeof DraftSummary>;

export const TrackSummary = z.object({
  id: Track,
  title: z.string(),
  palette: z.string(),
  packs: z.array(
    z.object({
      index: z.number().int().nonnegative(),
      first: z.number().int(),
      last: z.number().int(),
      chapter: z.string(),
      levelCount: count,
      sha256: z.string(),
      source: PackSource.optional(),
      publishedAt: z.string().nullable().optional(),
    }),
  ),
  /** Studio drafts (contracts/studio.md §5); absent from older servers. */
  drafts: z.array(DraftSummary).optional(),
  /** The index "Generate next pack" builds: after the last published pack, skipping drafts. */
  nextIndex: count.optional(),
  players: count,
  maxLevelReached: count,
});
export type TrackSummary = z.infer<typeof TrackSummary>;

export const Tracks = z.object({ tracks: z.array(TrackSummary) });

export const Level = z.object({
  n: z.number().int().min(1),
  flags: z.array(z.string()).optional(),
  spec: PuzzleSpecSchema,
});
export type Level = z.infer<typeof Level>;

export const LevelPack = z.looseObject({
  pack: z.literal(1),
  track: Track,
  index: z.number().int().nonnegative(),
  chapter: z.object({ name: z.string(), palette: z.string() }),
  requires: z.array(z.string()),
  generator: z.string().optional(),
  levels: z.array(Level).min(1).max(50),
});
export type LevelPack = z.infer<typeof LevelPack>;

export const LevelStats = z.object({ completions: count, avgStars: z.number().min(0).max(3), avgAttempts: z.number().min(0) });

const EngineCurvePoint = z.looseObject({ n: z.number().int(), type: z.string(), target: z.number().nullable(), score: z.number(), tier: z.number().int(), flags: z.array(z.string()) });

/** The engine's stats of a Studio pack: type distribution, difficulty curve (target and actual), intros, re-verification. */
export const PackEngineStats = z.looseObject({
  levels: count,
  types: z.record(z.string(), count),
  difficultyCurve: z.array(EngineCurvePoint),
  intros: z.array(z.object({ n: z.number().int(), type: z.string() })),
  verified: z.object({
    method: z.string(),
    checked: count,
    ok: count,
    failed: z.array(z.looseObject({ n: z.number().int(), id: z.string(), issues: z.array(z.string()) })),
  }),
});
export type PackEngineStats = z.infer<typeof PackEngineStats>;

export const PackStudioMeta = z.object({
  sha256: z.string(),
  /** { typeMix, difficulty: {start,end} | null, seed, custom, schedule, generator, history } */
  params: z.unknown(),
  stats: PackEngineStats.nullable(),
  jobId: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
  regenerated: z.array(z.looseObject({ n: z.number().int(), attempt: z.number().int(), at: z.string(), by: z.string().nullable(), type: z.string() })),
  publishedAt: z.string().nullable(),
  publishedBy: z.string().nullable(),
  stale: z.boolean(),
  canPublish: z.object({ ok: z.boolean(), reason: z.string().nullable() }),
});
export type PackStudioMeta = z.infer<typeof PackStudioMeta>;

export const PackResponse = z.object({
  /** draft: a Studio draft; absent from older servers (published). */
  status: z.enum(["published", "draft"]).optional(),
  source: PackSource.optional(),
  studio: PackStudioMeta.nullable().optional(),
  pack: LevelPack,
  /** levelId → answer recovered from check.digest. */
  answers: z.record(z.string(), AnswerSchema),
  /** levelIds whose answer could not be recovered from the digest. */
  unrecovered: z.array(z.string()),
  stats: z.record(z.string(), LevelStats),
});
export type PackResponse = z.infer<typeof PackResponse>;

// ---------------------------------------------------------------- players

/** The avatar orb, an index 0–5 into tokens `color.avatarOrbs` (Phase 3: the player's chosen orb). */
export const OrbIndex = z.number().int().min(0).max(5);

export const PlayerListItem = z.object({
  userId: z.string(),
  installId: z.string(),
  handle: z.string().nullable().default(null),
  createdAt: z.string(),
  streak: count,
  lastPlayedDate: isoDate.nullable(),
  orb: OrbIndex.nullable().default(null),
});
export type PlayerListItem = z.infer<typeof PlayerListItem>;

export const Players = z.object({ items: z.array(PlayerListItem) });

export const FlaggedSubmission = z.object({
  userId: z.string(),
  puzzleNumber: z.number().int(),
  date: isoDate,
  elapsedMs: z.number().nonnegative(),
  clientElapsedMs: z.number().nonnegative().nullable(),
  flags: z.array(IntegrityFlag),
  score: z.number().int().min(0).max(1000),
});
export type FlaggedSubmission = z.infer<typeof FlaggedSubmission>;

// ---------------------------------------------------------------- Phase 2 (contracts/phase2.md §1–§8, §12)

export const BadgeRarity = z.enum(["common", "uncommon", "rare", "epic"]);
export type BadgeRarity = z.infer<typeof BadgeRarity>;
export const RARITIES = BadgeRarity.options;

export const ReportReason = z.enum(["handle", "cheating", "other"]);
export type ReportReason = z.infer<typeof ReportReason>;
export const ReportStatus = z.enum(["open", "dismissed", "actioned"]);
export type ReportStatus = z.infer<typeof ReportStatus>;
export const ReportAction = z.enum(["reset_handle", "hide_from_leaderboards", "none"]);
export type ReportAction = z.infer<typeof ReportAction>;

/** 0–1 fraction; a 0–100 value is scaled down (admin responses use 0–100 for percentiles). */
const fraction = z.number().min(0).transform((v) => (v > 1 ? v / 100 : v));

const ReportParty = z.object({ userId: z.string(), handle: z.string().nullable().default(null) });

/** A queued report (POST /v1/users/{userId}/report), as the moderation queue lists it. */
export const Report = z.object({
  id,
  status: ReportStatus,
  reason: ReportReason,
  note: z.string().nullable().default(null),
  createdAt: z.string(),
  action: ReportAction.nullable().default(null),
  resolvedAt: z.string().nullable().default(null),
  /** Email of the resolving admin. */
  resolvedBy: z.string().nullable().default(null),
  reporter: ReportParty.nullable().default(null),
  reported: z.object({
    userId: z.string(),
    /** The current handle (null once reset). */
    handle: z.string().nullable().default(null),
    name: z.string().nullable().default(null),
    /** The handle when the report was filed. */
    handleAtReport: z.string().nullable().default(null),
    /** Held off the global leaderboards by moderation. */
    moderationHold: z.boolean().default(false),
    /** Every report about this player. */
    reports: count.default(1),
  }),
});
export type Report = z.infer<typeof Report>;

export const Reports = z.object({ items: z.array(Report), nextCursor: z.string().nullable().default(null) });
export type Reports = z.infer<typeof Reports>;

/** PATCH /reports/:id body (editor+, audited). */
export interface ReportPatch {
  status: "dismissed" | "actioned";
  action?: ReportAction;
}

/** An active support grant, as the Pro card shows it (derived from `entitlements` when the API doesn't send it). */
const GrantInfo = z.object({
  days: count,
  reason: z.string(),
  grantedBy: z.string().nullable().default(null),
  grantedAt: z.string().nullable().default(null),
  expiresAt: z.string().nullable().default(null),
});

/**
 * The player's Pro state (GET /v1/me `pro`, plus where it comes from). `plan` is monthly | annual |
 * lifetime | grant (derived from the product when an older backend doesn't send it); lifetime has
 * `expiresAt: null` and never renews.
 */
export const ProStatus = z
  .object({
    active: z.boolean(),
    productId: z.string().nullable().default(null),
    plan: z.string().nullable().default(null),
    expiresAt: z.string().nullable().default(null),
    inTrial: z.boolean().default(false),
    willRenew: z.boolean().default(false),
    /** `appstore` (a StoreKit purchase), `stripe` (bought on the web, contracts/web.md §5.6) or `grant` (support). */
    source: z.string().nullable().default(null),
    /** Where the player manages it: `appstore`, `stripe` (the Billing portal) or null (web.md §5.6). */
    manage: z.string().nullish(),
    /** Lifetime is active while a subscription still renews: the player should cancel it in Settings. */
    redundantSubscription: z.boolean().default(false),
    grant: GrantInfo.nullable().default(null),
  })
  .transform((p) => ({ ...p, plan: planOf(p) }));
export type ProStatus = z.infer<typeof ProStatus>;

/**
 * One entitlement row (App Store purchase, Stripe purchase or support grant), newest first. `expiresAt` is null for
 * lifetime. Stripe rows (contracts/web.md §5.1) carry `externalId` (sub_… or pi_…) and, when the API sends one,
 * `stripeUrl` (the customer in the Stripe dashboard); older backends send neither.
 */
export const Entitlement = z
  .object({
    id,
    source: z.string(),
    productId: z.string().nullable().default(null),
    plan: z.string().nullable().default(null),
    expiresAt: z.string().nullable().default(null),
    revokedAt: z.string().nullable().default(null),
    reason: z.string().nullable().default(null),
    createdAt: z.string(),
    grantedBy: z.string().nullable().default(null),
    externalId: z.string().nullish(),
    packageId: z.string().nullish(),
    inTrial: z.boolean().nullish(),
    willRenew: z.boolean().nullish(),
    stripeUrl: z.string().nullish(),
  })
  .transform((e) => ({ ...e, plan: planOf(e) }));
export type Entitlement = z.infer<typeof Entitlement>;

/** GET /v1/me `xp` (contracts/phase2.md §5). */
export const XpStatus = z.object({
  total: count,
  level: z.number().int().min(1),
  title: z.string(),
  into: count,
  needed: count,
  week: count,
});
export type XpStatus = z.infer<typeof XpStatus>;

/** An earned badge; names and rarity come from the catalogue when the API sends only ids. */
export const EarnedBadge = z.union([
  z.string().transform((badgeId) => ({ id: badgeId, name: null as string | null, rarity: null as BadgeRarity | null, earnedAt: null as string | null })),
  z.object({
    id: z.string(),
    name: z.string().nullable().default(null),
    rarity: BadgeRarity.nullable().default(null),
    earnedAt: z.string().nullable().default(null),
  }),
]);
export type EarnedBadge = z.infer<typeof EarnedBadge>;

/** A report about the player, as listed on their detail. */
export const PlayerReport = z.object({
  id,
  reason: ReportReason,
  status: ReportStatus,
  action: z.string().nullable().default(null),
  createdAt: z.string(),
  note: z.string().nullable().default(null),
});
export type PlayerReport = z.infer<typeof PlayerReport>;

const DAY_MS = 86_400_000;

/** The active support grant: `pro.grant` when sent, else the newest unrevoked, unexpired `source=grant` entitlement. */
function activeGrant(pro: ProStatus | null, entitlements: Entitlement[]): ProStatus["grant"] {
  if (pro?.grant) return pro.grant;
  const now = Date.now();
  // Support grants are always time-limited (only a lifetime purchase has no expiry).
  const g = entitlements.find((e) => e.source === "grant" && !e.revokedAt && e.expiresAt !== null && Date.parse(e.expiresAt) > now);
  if (!g?.expiresAt) return null;
  return {
    days: Math.max(1, Math.round((Date.parse(g.expiresAt) - Date.parse(g.createdAt)) / DAY_MS)),
    reason: g.reason ?? "",
    grantedBy: g.grantedBy,
    grantedAt: g.createdAt,
    expiresAt: g.expiresAt,
  };
}

/** Shapes per the backend implementation (contracts/admin-api.md "Implementation notes"). */
export const PlayerDetail = z.object({
  player: z.object({
    userId: z.string(),
    installId: z.string(),
    createdAt: z.string(),
    lastPlayedDate: isoDate.nullable(),
    /** Phase 1: the public handle (null when unset or reset) and linked sign-in providers. */
    handle: z.string().nullable().default(null),
    identities: z.array(z.string()).default([]),
    /** Phase 3: the chosen avatar orb (null when the backend doesn't send it). */
    orb: OrbIndex.nullable().default(null),
  }),
  streak: z.object({ current: count, best: count, lastPlayedDate: isoDate.nullable() }),
  daily: z.array(
    z.object({
      puzzleNumber: z.number().int(),
      date: isoDate,
      correct: z.boolean(),
      score: z.number().int().min(0).max(1000),
      elapsedMs: z.number().nonnegative(),
      clientElapsedMs: z.number().nonnegative().nullable(),
      flags: z.array(IntegrityFlag),
      /** 0–100 in admin responses; null below 200 players. */
      percentile: z.number().min(0).max(100).nullable(),
    }),
  ),
  levels: z.array(
    z.object({ track: Track, highestLevel: count, stars: count, completions: count, lastPlayedAt: z.string().nullable() }),
  ),
  flags: z.array(FlaggedSubmission),
  /** Phase 2 (contracts/phase2.md §12); defaults keep a Phase 1 backend parsing. */
  pro: ProStatus.nullable().default(null),
  entitlements: z.array(Entitlement).default([]),
  xp: XpStatus.nullable().default(null),
  badges: z.array(EarnedBadge).default([]),
  friendsCount: count.nullable().default(null),
  reports: z.array(PlayerReport).default([]),
  /** Held off the global leaderboards by moderation. */
  moderationHold: z.boolean().default(false),
  /** Phase 3: the player's team, when the API sends it. */
  team: z
    .object({ id, name: z.string(), role: z.string().nullable().default(null) })
    .nullable()
    .default(null),
}).transform((d) => (d.pro ? { ...d, pro: { ...d.pro, grant: activeGrant(d.pro, d.entitlements) } } : d));
export type PlayerDetail = z.infer<typeof PlayerDetail>;

// ---------------------------------------------------------------- monetisation (GET /paywall)

/** Paywall trigger sources (contracts/phase2.md §1), in display order. */
export const TRIGGER_SOURCES = ["stats", "archive", "hardmode", "freeze", "levels", "badge", "settings", "onboarding", "app_open", "exit_offer"] as const;
/** App Store products: the two subscriptions and the lifetime non-consumable. */
export const PRO_PRODUCTS = ["iqdaily.pro.monthly", "iqdaily.pro.annual", LIFETIME_PRODUCT] as const;

/** Pro players now, each under the plan that wins (lifetime first). */
const ActiveByPlan = z.object({ monthly: count.default(0), annual: count.default(0), lifetime: count.default(0), grant: count.default(0), total: count.optional() });

/** A count, or `{ shown|allowed|count, suppressed|refused }`. */
const ImpressionCount = z
  .union([
    count,
    z.looseObject({
      shown: count.optional(),
      allowed: count.optional(),
      count: count.optional(),
      total: count.optional(),
      suppressed: count.optional(),
      refused: count.optional(),
    }),
  ])
  .transform((v) =>
    typeof v === "number"
      ? { shown: v, suppressed: null as number | null }
      : { shown: v.shown ?? v.allowed ?? v.count ?? v.total ?? 0, suppressed: v.suppressed ?? v.refused ?? null },
  );

const PurchaseCount = z
  .union([count, z.looseObject({ count: count.optional(), purchases: count.optional(), total: count.optional(), trials: count.optional() })])
  .transform((v) =>
    typeof v === "number" ? { count: v, trials: null as number | null } : { count: v.count ?? v.purchases ?? v.total ?? 0, trials: v.trials ?? null },
  );

/** A rate (null when no paywall was shown), or `{ impressions, purchases, rate }`. */
const Conversion = z
  .union([fraction, z.null(), z.looseObject({ impressions: count.optional(), purchases: count.optional(), rate: fraction.nullable().optional() })])
  .transform((v) => {
    if (v === null || typeof v === "number") return { rate: v as number | null, impressions: null as number | null, purchases: null as number | null };
    const rate = v.rate ?? (v.impressions ? (v.purchases ?? 0) / v.impressions : null);
    return { rate, impressions: v.impressions ?? null, purchases: v.purchases ?? null };
  });

/**
 * GET /paywall. `impressions.bySource` counts paywalls shown; `refusedBySource` the ones the cap or
 * rules refused. `purchases.attributedBySource` counts purchases within 24 h of a shown paywall, and
 * `conversionBySource` is attributed ÷ shown. Refused and attributed counts are folded into the maps.
 */
export const Paywall = z
  .object({
    from: isoDate.optional(),
    to: isoDate.optional(),
    impressions: z.object({
      total: count.optional(),
      bySource: z.record(z.string(), ImpressionCount),
      refusedBySource: z.record(z.string(), count).optional(),
    }),
    purchases: z.object({
      total: count.optional(),
      byProduct: z.record(z.string(), PurchaseCount),
      /** New subscriptions and lifetime purchases, counted apart (derived from byProduct when absent). */
      subscriptions: count.optional(),
      lifetime: count.optional(),
      attributedBySource: z.record(z.string(), count).optional(),
    }),
    activeByPlan: ActiveByPlan.nullable().optional(),
    conversionBySource: z.record(z.string(), Conversion),
    /** Paywall 2 (contracts/paywall.md §5): paywalls shown in the range per experiment variant (the default paywall isn't a row). */
    byVariant: z
      .array(
        z.object({
          experimentId: z.string(),
          name: z.string(),
          variant: z.string(),
          shown: count,
          players: count,
          /** Players with a purchase attributed to this variant. */
          purchasers: count,
          /** purchasers ÷ players shown; null when none shown. */
          conversion: fraction.nullable(),
        }),
      )
      .optional(),
  })
  .transform((p) => {
    const refused = p.impressions.refusedBySource ?? {};
    const attributed = p.purchases.attributedBySource ?? {};
    const bySource = Object.fromEntries(
      [...new Set([...Object.keys(p.impressions.bySource), ...Object.keys(refused)])].map((s) => {
        const v = p.impressions.bySource[s] ?? { shown: 0, suppressed: null };
        return [s, { shown: v.shown, suppressed: refused[s] ?? v.suppressed }];
      }),
    );
    const conversionBySource = Object.fromEntries(
      Object.entries(p.conversionBySource).map(([s, c]) => [
        s,
        { rate: c.rate, impressions: c.impressions ?? bySource[s]?.shown ?? null, purchases: c.purchases ?? attributed[s] ?? null },
      ]),
    );
    const lifetime = p.purchases.lifetime ?? p.purchases.byProduct[LIFETIME_PRODUCT]?.count ?? 0;
    const subscriptions =
      p.purchases.subscriptions ??
      Object.entries(p.purchases.byProduct).reduce((a, [product, v]) => (product === LIFETIME_PRODUCT ? a : a + v.count), 0);
    const active = p.activeByPlan ? { ...p.activeByPlan, total: p.activeByPlan.total ?? p.activeByPlan.monthly + p.activeByPlan.annual + p.activeByPlan.lifetime + p.activeByPlan.grant } : null;
    return {
      from: p.from,
      to: p.to,
      impressions: { total: p.impressions.total, bySource },
      purchases: { total: p.purchases.total, byProduct: p.purchases.byProduct, subscriptions, lifetime },
      activeByPlan: active,
      conversionBySource,
      byVariant: p.byVariant ?? null,
    };
  });
export type Paywall = z.infer<typeof Paywall>;

// ---------------------------------------------------------------- badges (GET /badges)

export const BadgeStat = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  rarity: BadgeRarity,
  hidden: z.boolean().default(false),
  /** Progress target (e.g. 7 for a 7-day streak), when the rule has one. */
  target: count.nullable().default(null),
  holders: count,
  /** Active holders ÷ players active in the last 30 days, 0–1 (0–100 is accepted). */
  holderPct: z.number().min(0).nullable().default(null),
});
export type BadgeStat = z.infer<typeof BadgeStat>;

export const BadgeCatalogue = z
  .union([
    z.object({ activePlayers: count.nullable().default(null), players: count.nullable().default(null), items: z.array(BadgeStat) }),
    z
      .object({ activePlayers: count.nullable().default(null), players: count.nullable().default(null), badges: z.array(BadgeStat) })
      .transform(({ badges, ...rest }) => ({ ...rest, items: badges })),
    z.array(BadgeStat).transform((items) => ({ activePlayers: null, players: null, items })),
  ])
  .transform(({ activePlayers, players, items }) => {
    // holderPct as a fraction: 0–100 when any value is above 1 (the most common badge sits well above 1%).
    const percent = items.some((b) => (b.holderPct ?? 0) > 1);
    return {
      activePlayers,
      players,
      badges: items.map((b) => ({
        ...b,
        holderPct:
          b.holderPct !== null ? (percent ? b.holderPct / 100 : b.holderPct) : activePlayers ? Math.min(1, b.holders / activePlayers) : null,
      })),
    };
  });
export type BadgeCatalogue = z.infer<typeof BadgeCatalogue>;

// ---------------------------------------------------------------- leaderboards (GET /leaderboards/:board)

export const LEADERBOARDS = ["daily", "hard", "xp-week", "xp-all", "streak"] as const;
export type LeaderboardName = (typeof LEADERBOARDS)[number];

export const LeaderboardEntry = z.object({
  rank: z.number().int().min(1),
  userId: z.string(),
  name: z.string(),
  handle: z.string().nullable().default(null),
  value: z.number(),
  /** Hidden from the global board players see (opted out, or a moderation hold). */
  hidden: z.boolean().default(false),
  moderationHold: z.boolean().default(false),
});
export type LeaderboardEntry = z.infer<typeof LeaderboardEntry>;

export const Leaderboard = z.object({
  board: z.string(),
  /** Puzzle number for daily and hard boards. */
  n: z.number().int().nullable().default(null),
  /** ISO week for xp-week (e.g. 2026-W39). */
  week: z.string().nullable().default(null),
  total: count,
  entries: z.array(LeaderboardEntry).max(100),
});
export type Leaderboard = z.infer<typeof Leaderboard>;

// ---------------------------------------------------------------- Phase 3: teams (contracts/phase3.md "Web and admin")
//
// Shapes per backend/api (schemas/teams.ts: AdminTeams, AdminTeamDetail, AdminDivisions,
// TeamsCloseReport); the variants noted on each schema are accepted too.

export const TeamPolicy = z.enum(["invite_only", "open_link", "public"]);
export type TeamPolicy = z.infer<typeof TeamPolicy>;
export const TeamZone = z.enum(["promotion", "safe", "relegation"]);
export type TeamZone = z.infer<typeof TeamZone>;
export const WeekOutcome = z.enum(["promoted", "stayed", "relegated", "dormant"]);
export type WeekOutcome = z.infer<typeof WeekOutcome>;
export const WeekStatus = z.enum(["open", "pending", "closed"]);
export type WeekStatus = z.infer<typeof WeekStatus>;

/** ISO week in UTC, e.g. 2026-W39. */
export const isoWeekId = z.string().regex(/^\d{4}-W\d{2}$/, "expected YYYY-Www");

/** The current tournament week (UTC). */
export const currentIsoWeek = () => isoWeekOf(todayUtc());

const nullableNumber = z.number().nullable().default(null);

/** `{ tier, index }`, a bare tier, or null before placement / while dormant. */
const DivisionRef = z
  .union([z.object({ tier: z.number().int().min(1), index: z.number().int().min(0).nullable().default(null) }), z.number().int().min(1)])
  .nullable()
  .default(null)
  .transform((d) => (typeof d === "number" ? { tier: d, index: null as number | null } : d));
export type DivisionRef = z.output<typeof DivisionRef>;

/** A count, or an array (its length). */
const countOrList = z.union([count, z.array(z.unknown()).transform((a) => a.length)]);

/** The emblem when the API sends one (normalised), else null: the panel never invents a team's emblem. */
const OptionalEmblem = z.unknown().optional();
const emblemOrNull = (raw: unknown, name: string): Emblem | null => (raw === undefined || raw === null ? null : normaliseEmblem(raw, name));

const TeamStatus = z.enum(["active", "dormant", "disbanded"]);

const TeamBase = z.object({
  id,
  name: z.string(),
  /** `inviteCode` (backend/api) or `code`. */
  inviteCode: z.string().optional(),
  code: z.string().nullable().optional(),
  emblem: z.unknown().optional(),
  cap: count.default(8),
  members: countOrList.optional(),
  memberCount: count.optional(),
  policy: TeamPolicy.nullable().default(null),
  captain: z
    .object({ userId: z.string(), name: z.string().nullable().default(null), handle: z.string().nullable().default(null) })
    .nullable()
    .default(null),
  division: DivisionRef,
  /** Not in backend/api's list; the panel ranks from the live division tables instead. */
  rankThisWeek: z.number().int().nullable().default(null),
  weekScore: nullableNumber,
  streakWeeks: count.default(0),
  status: TeamStatus.default("active"),
  createdAt: z.string().nullable().default(null),
  disbandedAt: z.string().nullable().default(null),
  inviteUrl: z.string().nullable().default(null),
});

const toTeam = <T extends z.output<typeof TeamBase>>({ members, memberCount, inviteCode, code, emblem, ...t }: T) => ({
  ...t,
  code: code ?? inviteCode ?? null,
  memberCount: memberCount ?? members ?? 0,
  emblem: normaliseEmblem(emblem, t.name),
});

/** GET /teams?q=&status= rows (AdminTeamItem). */
export const TeamListItem = TeamBase.transform(toTeam);
export type TeamListItem = z.output<typeof TeamListItem>;

/** `{ week, items }` (backend/api), `{ items, nextCursor }`, `{ teams }` or a bare array. */
export const TeamList = z.union([
  z.object({ week: isoWeekId.optional(), items: z.array(TeamListItem), nextCursor: z.string().nullable().default(null) }),
  z.object({ teams: z.array(TeamListItem), nextCursor: z.string().nullable().default(null) }).transform(({ teams, ...r }) => ({ ...r, items: teams })),
  z.array(TeamListItem).transform((items) => ({ items, nextCursor: null as string | null })),
]);
export type TeamList = z.output<typeof TeamList>;

/** A member this week. `completed` is the whole set done (or puzzles played); `played` 0–10; `counting` is derived when absent. */
export const TeamMember = z
  .object({
    userId: z.string(),
    name: z.string(),
    handle: z.string().nullable().default(null),
    orb: OrbIndex.nullable().default(null),
    role: z.enum(["captain", "member"]).default("member"),
    joinedAt: z.string().nullable().default(null),
    setScore: nullableNumber,
    completed: z.union([count, z.boolean()]).nullable().default(null),
    played: count.optional(),
    joinedMidWeek: z.boolean().default(false),
    counting: z.boolean().optional(),
  })
  .transform(({ completed, played, ...m }) => {
    const n = played ?? (typeof completed === "number" ? completed : completed === true ? 10 : null);
    return { ...m, played: n, setDone: completed === true || (n ?? 0) >= 10 };
  });
export type TeamMember = z.output<typeof TeamMember> & { counting: boolean };

/** One week of the team's history: closed weeks (backend/api), plus the live one in some variants. */
export const TeamWeek = z
  .object({
    week: isoWeekId,
    tier: z.number().int().min(1).nullable().default(null),
    /** `divisionIndex` (backend/api) or `index`. */
    divisionIndex: z.number().int().min(0).nullable().optional(),
    index: z.number().int().min(0).nullable().optional(),
    rank: z.number().int().min(1).nullable().default(null),
    of: count.nullable().default(null),
    score: nullableNumber,
    sets: count.nullable().default(null),
    members: count.nullable().default(null),
    zone: TeamZone.nullable().default(null),
    outcome: WeekOutcome.nullable().default(null),
    newTier: z.number().int().nullable().default(null),
    dormant: z.boolean().default(false),
    bestSolver: z.object({ name: z.string(), setScore: z.number() }).nullable().default(null),
  })
  .transform(({ divisionIndex, index, dormant, outcome, ...w }) => ({
    ...w,
    index: divisionIndex ?? index ?? null,
    outcome: dormant ? ("dormant" as const) : outcome,
  }));
export type TeamWeek = z.output<typeof TeamWeek>;

/**
 * `team_events` rows, newest first. backend/api: `{ id, kind, actorId, targetId, adminEmail, payload, createdAt }`;
 * `type`/`at`, an `actor` string or object and a `subject` are accepted too. Names come from the members list.
 */
export const TeamEvent = z
  .object({
    id,
    type: z.string().optional(),
    kind: z.string().optional(),
    at: z.string().optional(),
    createdAt: z.string().optional(),
    actor: z
      .union([z.string(), z.object({ userId: z.string().nullable().default(null), name: z.string().nullable().default(null), email: z.string().nullable().default(null) })])
      .nullable()
      .default(null),
    actorId: z.string().nullable().default(null),
    targetId: z.string().nullable().default(null),
    adminEmail: z.string().nullable().default(null),
    subject: z.object({ userId: z.string(), name: z.string().nullable().default(null) }).nullable().default(null),
    payload: z.unknown().optional(),
  })
  .transform((e) => ({
    id: e.id,
    kind: e.kind ?? e.type ?? "event",
    at: e.at ?? e.createdAt ?? "",
    /** A display name or an admin email, when the API gives one. */
    actor: e.adminEmail ?? (typeof e.actor === "string" ? e.actor : (e.actor?.email ?? e.actor?.name ?? null)),
    actorId: e.actorId ?? (typeof e.actor === "object" && e.actor ? e.actor.userId : null),
    admin: e.adminEmail !== null,
    subject: e.subject ?? (e.targetId ? { userId: e.targetId, name: null as string | null } : null),
    payload: e.payload ?? null,
  }));
export type TeamEvent = z.output<typeof TeamEvent>;

export const TeamDetail = z
  .object({
    team: TeamBase,
    members: z.array(TeamMember).default([]),
    weeks: z.array(TeamWeek).optional(),
    history: z.array(TeamWeek).optional(),
    events: z.array(TeamEvent).default([]),
  })
  .transform(({ weeks, history, team, members, ...d }) => {
    // backend/api doesn't flag the counting sets: the best five set scores above 0.
    const derive = members.every((m) => m.counting === undefined);
    const best = new Set(
      [...members]
        .filter((m) => (m.setScore ?? 0) > 0)
        .sort((a, b) => (b.setScore ?? 0) - (a.setScore ?? 0))
        .slice(0, 5)
        .map((m) => m.userId),
    );
    return {
      ...d,
      team: toTeam(team),
      members: members.map((m) => ({ ...m, counting: derive ? best.has(m.userId) : Boolean(m.counting) })),
      weeks: weeks ?? history ?? [],
    };
  });
export type TeamDetail = z.output<typeof TeamDetail>;

export const DivisionTeam = z
  .object({
    rank: z.number().int().min(1),
    teamId: id,
    name: z.string(),
    emblem: OptionalEmblem,
    score: z.number(),
    sets: count.nullable().default(null),
    members: countOrList.nullable().default(null),
    zone: TeamZone.nullable().default(null),
    outcome: WeekOutcome.nullable().default(null),
    newTier: z.number().int().nullable().default(null),
  })
  .transform((t) => ({ ...t, emblem: emblemOrNull(t.emblem, t.name) }));
export type DivisionTeam = z.output<typeof DivisionTeam>;

const DivisionTable = z.object({
  /** Inside `tiers[]` the tier comes from the parent (backend/api omits it). */
  tier: z.number().int().min(1).optional(),
  index: z.number().int().min(0),
  teams: z.array(DivisionTeam).optional(),
  standings: z.array(DivisionTeam).optional(),
});

/** How many weeks the week pickers offer when the API doesn't list them. */
export const WEEKS_SHOWN = 12;

/**
 * GET /divisions?week= (backend/api: `{ week, live, closedAt, lowestTier, tiers: [{ tier, divisions: [{ index, teams }] }] }`).
 * A flat `divisions: [{ tier, index, teams | standings }]`, a `status` and a `weeks` list are accepted too.
 * `status`: `closed` (a snapshot), `open` (this week, live) or `pending` (ended, not closed yet). Missing zones
 * are derived from the contract; missing week lists become the last 12 ISO weeks.
 */
export const Divisions = z
  .object({
    week: isoWeekId,
    status: WeekStatus.nullable().default(null),
    live: z.boolean().optional(),
    closedAt: z.string().nullable().default(null),
    lowestTier: z.number().int().min(1).optional(),
    weeks: z.array(z.union([isoWeekId, z.object({ week: isoWeekId, status: WeekStatus.nullable().default(null) })])).default([]),
    tiers: z.array(z.object({ tier: z.number().int().min(1), divisions: z.array(DivisionTable) })).optional(),
    divisions: z.array(DivisionTable.extend({ tier: z.number().int().min(1) })).optional(),
  })
  .transform(({ tiers, divisions, weeks, live, lowestTier, status, ...d }) => {
    const flat = tiers
      ? tiers.flatMap((t) => t.divisions.map((x) => ({ ...x, tier: x.tier ?? t.tier })))
      : (divisions ?? []).map((x) => ({ ...x, tier: x.tier }));
    const lowest = lowestTier ?? Math.max(1, ...flat.map((x) => x.tier));
    const byTier = new Map<number, { tier: number; index: number; teams: DivisionTeam[] }[]>();
    for (const x of flat) {
      const teams = [...(x.teams ?? x.standings ?? [])]
        .sort((a, b) => a.rank - b.rank)
        .map((t) => ({ ...t, zone: t.zone ?? zoneFor(t.rank, x.tier, lowest) }));
      byTier.set(x.tier, [...(byTier.get(x.tier) ?? []), { tier: x.tier, index: x.index, teams }]);
    }
    const current = currentIsoWeek();
    const derived: WeekStatus = status ?? (d.closedAt || live === false ? "closed" : d.week === current ? "open" : "pending");
    const listed = weeks.map((w) => (typeof w === "string" ? { week: w, status: null as WeekStatus | null } : w));
    const list = listed.length
      ? listed
      : recentIsoWeeks(current > d.week ? current : d.week, WEEKS_SHOWN).map((w) => ({ week: w, status: w === d.week ? derived : w === current ? ("open" as const) : null }));
    return {
      ...d,
      status: derived,
      weeks: list,
      lowestTier: lowest,
      tiers: [...byTier.entries()].sort((a, b) => a[0] - b[0]).map(([tier, divs]) => ({ tier, divisions: divs.sort((a, b) => a.index - b.index) })),
    };
  });
export type Divisions = z.output<typeof Divisions>;

/** contracts/phase3.md "Divisions": ranks 1–3 promoted (not from tier 1), 16–18 relegated (not from the lowest tier). */
export function zoneFor(rank: number, tier: number, lowestTier: number): TeamZone {
  if (rank <= 3 && tier > 1) return "promotion";
  if (rank >= 16 && tier < lowestTier) return "relegation";
  return "safe";
}

const TeamMoveIn = z.object({
  teamId: id,
  name: z.string(),
  emblem: OptionalEmblem,
  tier: z.number().int().min(1).nullable().default(null),
  fromTier: z.number().int().min(1).optional(),
  toTier: z.number().int().min(1).nullable().optional(),
  newTier: z.number().int().min(1).nullable().optional(),
  index: z.number().int().min(0).nullable().default(null),
  newIndex: z.number().int().min(0).nullable().default(null),
  rank: z.number().int().min(1).nullable().default(null),
  of: count.nullable().default(null),
  score: nullableNumber,
  sets: count.nullable().default(null),
  zone: TeamZone.nullable().default(null),
  outcome: WeekOutcome.nullable().default(null),
  dormant: z.boolean().default(false),
  weeksInactive: count.nullable().default(null),
});
const toMove = ({ fromTier, toTier, newTier, emblem, ...m }: z.output<typeof TeamMoveIn>) => ({
  ...m,
  emblem: emblemOrNull(emblem, m.name),
  tier: m.tier ?? fromTier ?? null,
  toTier: toTier ?? newTier ?? null,
});
const TeamMove = TeamMoveIn.transform(toMove);
export type TeamMove = z.output<typeof TeamMove>;

/**
 * POST /jobs/teams-close `{ week, dryRun }` (admin only). backend/api returns a TeamsCloseReport:
 * `{ week, dryRun, alreadyClosed, closedAt, teams, divisions, lowestTier, promoted, relegated, stayed,
 * dormant (a count), xp, badges, moves: [every team's outcome] }`; the lists are split out here.
 * `{ promotions, relegations, dormant: [...] }` lists are accepted too. A dry run changes nothing;
 * a real run needs a recent dry run of the same week (409 otherwise). A closed week returns its report.
 */
export const TeamsCloseResult = z
  .object({
    week: isoWeekId,
    dryRun: z.boolean(),
    status: WeekStatus.nullable().default(null),
    alreadyClosed: z.boolean().optional(),
    closedAt: z.string().nullable().default(null),
    divisions: count.nullable().default(null),
    teams: count.nullable().default(null),
    lowestTier: z.number().int().min(1).nullable().default(null),
    moves: z.array(TeamMoveIn).optional(),
    promotions: z.array(TeamMoveIn).optional(),
    relegations: z.array(TeamMoveIn).optional(),
    dormant: z.union([z.array(TeamMoveIn), count]).optional(),
    promoted: count.optional(),
    relegated: count.optional(),
    stayed: count.optional(),
    xp: z.record(z.string(), z.number()).default({}),
    badges: z.record(z.string(), z.number()).default({}),
    /** Echoed back on the real run when a server hands one out with the dry run. */
    token: z.string().nullable().default(null),
  })
  .transform(({ moves, promotions, relegations, dormant, promoted, relegated, stayed, alreadyClosed, status, ...r }) => {
    const all = (moves ?? []).map(toMove);
    const up = promotions ? promotions.map(toMove) : all.filter((m) => !m.dormant && m.outcome === "promoted");
    const down = relegations ? relegations.map(toMove) : all.filter((m) => !m.dormant && m.outcome === "relegated");
    const idle = Array.isArray(dormant) ? dormant.map(toMove) : all.filter((m) => m.dormant || m.outcome === "dormant");
    return {
      ...r,
      status: status ?? (alreadyClosed || (!r.dryRun && r.closedAt) ? ("closed" as const) : r.week === currentIsoWeek() ? ("open" as const) : ("pending" as const)),
      promotions: up,
      relegations: down,
      dormant: idle,
      counts: {
        promoted: promoted ?? up.length,
        relegated: relegated ?? down.length,
        dormant: typeof dormant === "number" ? dormant : idle.length,
        stayed: stayed ?? (all.length ? all.length - up.length - down.length - idle.length : null),
      },
    };
  });
export type TeamsCloseResult = z.output<typeof TeamsCloseResult>;

// ---------------------------------------------------------------- Phase 4: arena, duels, rooms (contracts/phase4.md "Admin")
//
// The contract names the admin routes, not their shapes. The panel reads the shapes below (the mock
// serves them); each schema also accepts the variants noted on it. Match detail is the Go service's
// `kind: "match"` results payload (contracts/realtime/README.md §7) as Node stores it.

/** An array that may come back as null (Go marshals an empty slice as null). */
const list = <T extends z.ZodType>(item: T) =>
  z
    .array(item)
    .nullish()
    .transform((v) => v ?? []);

/** A 0–1 share; a 0–100 percentage is scaled down. */
const share = z
  .number()
  .min(0)
  .nullable()
  .default(null)
  .transform((v) => (v === null ? null : v > 1 ? v / 100 : v));

/** An avatar orb: an index 0–5, a glow name, or null. */
const ORB_BY_NAME: Record<string, number> = { teal: 0, azure: 1, ember: 2, violet: 3, magenta: 4, lime: 5, moss: 5 };
const orbRef = z
  .union([z.number().int(), z.string()])
  .nullable()
  .default(null)
  .transform((v) => (v === null ? null : typeof v === "number" ? ((v % 6) + 6) % 6 : /^\d+$/.test(v) ? Number(v) % 6 : (ORB_BY_NAME[v.toLowerCase()] ?? null)));

export const DropState = z.enum(["scheduled", "lobby", "running", "finished", "rolled_over", "aborted"]);
export type DropState = z.infer<typeof DropState>;
const DROP_STATE_ALIASES: Record<string, DropState> = {
  scheduled: "scheduled",
  upcoming: "scheduled",
  lobby: "lobby",
  open: "lobby",
  running: "running",
  live: "running",
  playing: "running",
  finished: "finished",
  played: "finished",
  done: "finished",
  complete: "finished",
  completed: "finished",
  ended: "finished",
  rolled_over: "rolled_over",
  rollover: "rolled_over",
  rolledover: "rolled_over",
  not_enough_players: "rolled_over",
  aborted: "aborted",
};

/** One match of a drop, as the drops list carries it. */
export const ArenaMatchSummary = z.object({
  matchId: z.string(),
  players: count.default(0),
  humans: count.optional(),
  bots: count.default(0),
  startedAt: z.string().nullable().default(null),
  endedAt: z.string().nullable().default(null),
  aborted: z.boolean().default(false),
  winner: z
    .object({ userId: z.string().nullable().default(null), seat: z.number().int().nullable().default(null), name: z.string(), isBot: z.boolean().default(false) })
    .nullable()
    .default(null),
  cutRounds: count.nullable().default(null),
  finalPoints: count.nullable().default(null),
  completion: share,
  medianRttMs: nullableNumber,
  flaggedAnswers: count.default(0),
  /** Phase 4 follow-up: the results webhook's `dev` flag (the realtime service ran outside production). */
  dev: z.boolean().optional(),
});
export type ArenaMatchSummary = z.output<typeof ArenaMatchSummary>;

/** Phase 4 follow-up: why backend/api counts a drop as dev (contracts/phase4.md "Dev drops"). */
export const DEV_DROP_REASONS = ["adhoc", "dev_match", "bots", "off_schedule"] as const;
export type DevDropReason = (typeof DEV_DROP_REASONS)[number];
export const DEV_REASON_LABEL: Record<string, string> = {
  adhoc: "ad-hoc drop from the realtime admin",
  dev_match: "a match reported dev: true",
  bots: "practice bots (dev and QA only)",
  off_schedule: "a DEV_EVERY_MINUTES drop, off arena.schedule",
};

/**
 * A drop row of `GET /arena/drops?from&to`. backend/api: `{ dropId, region, t0, state: scheduled | lobby |
 * played | rolled_over, entrants, players, humans, bots, matches (a count), completedMatches, completion,
 * fill, medianRttMs, flaggedAnswers, reservations, rolledOverTo, matchList: [match] }`. Also accepted:
 * `matches` as the list, `id`, `status`, `rolledOver`, `nextDropId`, `dev`; missing states and fill are
 * derived. `fill` is players ÷ (100 × matches); `completion` completed matches ÷ matches. Dev drops
 * are `…-adhoc` ids (backend/api), `dev-…` ones, or any drop with practice bots.
 */
export const ArenaDrop = z
  .object({
    dropId: z.string().optional(),
    id: z.string().optional(),
    t0: z.string(),
    region: z.string().default("global"),
    state: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    rolledOver: z.boolean().optional(),
    entrants: count.default(0),
    players: count.optional(),
    humans: count.optional(),
    bots: count.optional(),
    capacity: count.default(100),
    matches: z.union([count, z.array(ArenaMatchSummary)]).default(0),
    matchList: z.array(ArenaMatchSummary).nullish(),
    matchCount: count.optional(),
    completedMatches: count.optional(),
    fill: share,
    completion: share,
    medianRttMs: nullableNumber,
    flaggedAnswers: count.default(0),
    reservations: count.optional(),
    nextDropId: z.string().nullable().default(null),
    rolledOverTo: z.string().nullable().optional(),
    dev: z.boolean().optional(),
    /** Phase 4 follow-up: adhoc | dev_match | bots | off_schedule (unknown reasons are kept). */
    devReasons: z.array(z.string()).optional(),
  })
  .transform((d) => {
    const list = d.matchList ?? (Array.isArray(d.matches) ? d.matches : []);
    const matchCount = d.matchCount ?? (typeof d.matches === "number" ? d.matches : list.length);
    const raw = String(d.state ?? d.status ?? "").toLowerCase();
    const past = Date.parse(d.t0) <= Date.now();
    const state: DropState =
      DROP_STATE_ALIASES[raw] ?? (d.rolledOver ? "rolled_over" : matchCount > 0 ? "finished" : past ? (d.entrants > 0 ? "rolled_over" : "finished") : "scheduled");
    const id = d.dropId ?? d.id ?? d.t0;
    const t0 = new Date(d.t0).toISOString();
    const players = d.players ?? d.entrants;
    return {
      id,
      t0,
      /** The drop's UTC date (drops are listed by date). */
      date: t0.slice(0, 10),
      region: d.region,
      state,
      entrants: Math.max(d.entrants, players),
      capacity: d.capacity,
      matchCount,
      matches: list,
      fill: d.fill ?? (d.capacity > 0 && players > 0 ? players / (d.capacity * Math.max(1, matchCount)) : null),
      completion: d.completion,
      medianRttMs: d.medianRttMs,
      flaggedAnswers: d.flaggedAnswers,
      reservations: d.reservations ?? null,
      nextDropId: d.rolledOverTo ?? d.nextDropId,
      // backend/api says why a drop is dev (Phase 4 follow-ups); an older API gets the reasons inferred:
      // an ad-hoc id, a match reported dev, practice bots (they only exist in dev and QA).
      ...devOf(d.dev, d.devReasons, id, d.bots ?? list.reduce((n, m) => n + m.bots, 0), list),
      bots: d.bots ?? list.reduce((n, m) => n + m.bots, 0),
    };
  });

function devOf(dev: boolean | undefined, reasons: string[] | undefined, id: string, bots: number, matches: { dev?: boolean | undefined }[]) {
  const devReasons =
    reasons ??
    [
      /-adhoc$/.test(id) || /^dev-/.test(id) ? "adhoc" : null,
      matches.some((m) => m.dev) ? "dev_match" : null,
      bots > 0 ? "bots" : null,
    ].filter((r): r is string => r !== null);
  return { dev: dev ?? devReasons.length > 0, devReasons };
}
export type ArenaDrop = z.output<typeof ArenaDrop>;

/** `{ from, to, items }`, `{ drops }` or a bare array; newest first. */
export const ArenaDrops = z
  .union([
    z.object({ from: z.string().optional(), to: z.string().optional(), items: z.array(ArenaDrop) }),
    z.object({ from: z.string().optional(), to: z.string().optional(), drops: z.array(ArenaDrop) }).transform(({ drops, ...r }) => ({ ...r, items: drops })),
    z.array(ArenaDrop).transform((items) => ({ items, from: undefined as string | undefined, to: undefined as string | undefined })),
  ])
  .transform((r) => ({ from: r.from ?? null, to: r.to ?? null, items: [...r.items].sort((a, b) => b.t0.localeCompare(a.t0)) }));
export type ArenaDrops = z.output<typeof ArenaDrops>;

export const PlacementReason = z.enum(["winner", "final", "cut", "forfeit", "excluded", "aborted"]);
export type PlacementReason = z.infer<typeof PlacementReason>;

export const ArenaPlacement = z.object({
  userId: z.string().nullable().default(null),
  name: z.string().default(""),
  orb: orbRef,
  seat: z.number().int().min(0),
  isBot: z.boolean().default(false),
  /** 1 = winner; 0 = unplaced (aborted). */
  placement: count.default(0),
  reason: z
    .string()
    .default("cut")
    .transform((r) => (PlacementReason.safeParse(r).success ? (r as PlacementReason) : ("cut" as const))),
  eliminatedRound: count.default(0),
  roundsSurvived: count.default(0),
  answered: count.default(0),
  correct: count.default(0),
  avgMs: nullableNumber,
  fastestMs: nullableNumber,
  flags: count.default(0),
  excluded: z.boolean().default(false),
  xp: count.default(0),
  crowns: count.default(0),
});
export type ArenaPlacement = z.output<typeof ArenaPlacement>;

export const ArenaAnswer = z.object({
  seat: z.number().int().min(0),
  rank: count.nullable().default(null),
  correct: z.boolean(),
  answerMs: z.number().min(0),
  bucketMs: nullableNumber,
  recvOffsetMs: nullableNumber,
  oneWayMs: nullableNumber,
  clientElapsedMs: nullableNumber,
  flagged: z.boolean().default(false),
});
export type ArenaAnswer = z.output<typeof ArenaAnswer>;

export const ArenaRound = z.object({
  /** Match-wide; keeps counting through the final. */
  round: z.number().int().min(1),
  displayRound: z.number().int().nullable().default(null),
  final: z.boolean().default(false),
  /** Final points: which point (a replayed point repeats). */
  point: z.number().int().nullable().default(null),
  question: z.string().default(""),
  operation: z.string().nullable().default(null),
  difficulty: z.number().nullable().default(null),
  goAt: z.string().nullable().default(null),
  closedAt: z.string().nullable().default(null),
  alive: count.default(0),
  cutAfter: count.nullable().default(null),
  cut: list(z.number().int().min(0)),
  pointWinnerSeat: z.number().int().nullable().default(null),
  answers: list(ArenaAnswer),
});
export type ArenaRound = z.output<typeof ArenaRound>;

const ArenaMatchBody = z.object({
  matchId: z.string(),
  dropId: z.string().nullable().default(null),
  region: z.string().default("global"),
  startedAt: z.string().nullable().default(null),
  endedAt: z.string().nullable().default(null),
  players: count.default(0),
  cutRounds: count.nullable().default(null),
  finalPoints: count.nullable().default(null),
  aborted: z.boolean().default(false),
  abortReason: z.string().nullable().default(null),
  winner: z
    .object({ userId: z.string().nullable().default(null), seat: z.number().int().nullable().default(null), name: z.string(), isBot: z.boolean().default(false) })
    .nullable()
    .default(null),
  placements: list(ArenaPlacement),
  rounds: list(ArenaRound),
  medianRttMs: nullableNumber,
  flaggedAnswers: count.default(0),
  dev: z.boolean().default(false),
});

/**
 * `GET /arena/matches/:id`: the round-by-round replay, i.e. the results webhook's `kind: "match"`
 * payload (`{ matchId, dropId, players, placements, rounds, medianRttMs, flaggedAnswers, … }`),
 * bare or as `{ match }`. Rounds are sorted; `players` falls back to the placements.
 */
export const ArenaMatch = z
  .union([ArenaMatchBody, z.object({ match: ArenaMatchBody }).transform((b) => b.match)])
  .transform((m) => ({
    ...m,
    players: m.players || m.placements.length,
    placements: [...m.placements].sort((a, b) => (a.placement || 1e9) - (b.placement || 1e9) || a.seat - b.seat),
    rounds: [...m.rounds].sort((a, b) => a.round - b.round),
  }));
export type ArenaMatch = z.output<typeof ArenaMatch>;

// Duels (contracts/phase4.md §B).

/** backend/api's duel statuses: queued (a ladder request waiting), pending, choosing (a series game's type), active, then settled, declined, expired, cancelled. */
export const DuelStatus = z.enum(["queued", "pending", "choosing", "active", "settled", "declined", "expired", "cancelled"]);
export type DuelStatus = z.infer<typeof DuelStatus>;
export const OPEN_DUEL_STATUSES: DuelStatus[] = ["queued", "pending", "choosing", "active"];
const DUEL_STATUS_ALIASES: Record<string, DuelStatus> = {
  ...Object.fromEntries(DuelStatus.options.map((s) => [s, s])),
  open: "pending",
  invited: "pending",
  matching: "queued",
  accepted: "active",
  playing: "active",
  finished: "settled",
  complete: "settled",
  completed: "settled",
  canceled: "cancelled",
};
export const DuelMode = z.enum(["friend", "ladder", "series"]);
export type DuelMode = z.infer<typeof DuelMode>;

/** One puzzle of a side's set (G6's per-puzzle breakdown), when the API includes it. */
export const DuelPuzzle = z.object({
  /** 0-based in backend/api (as /v1/sets/{setId}/{index}). */
  index: count.optional(),
  puzzleId: z.string().nullable().default(null),
  type: z.string().nullable().default(null),
  /** backend/api: the puzzle's calibrated difficulty, 0–1. */
  difficulty: z.number().nullable().default(null),
  correct: z.boolean().nullable().default(null),
  /** backend/api: the puzzle score, 0–1000. */
  points: nullableNumber,
  elapsedMs: nullableNumber,
  // Phase 4 follow-ups (GET /duels/:id): timings and anti-cheat flags, never the answer.
  category: z.string().nullable().default(null),
  started: z.boolean().nullable().default(null),
  startedAt: z.string().nullable().default(null),
  submittedAt: z.string().nullable().default(null),
  clientElapsedMs: nullableNumber,
  flags: z.array(z.string()).nullish().transform((v) => v ?? []),
});

const DuelSideIn = z.object({
  userId: z.string().nullable().default(null),
  name: z.string().nullable().default(null),
  handle: z.string().nullable().default(null),
  orb: orbRef,
  /** The rating going in (`ratingBefore` accepted). */
  rating: nullableNumber,
  ratingBefore: z.number().nullable().optional(),
  ratingAfter: nullableNumber,
  ratingDelta: nullableNumber,
  /** backend/api's list calls the rating change `delta`. */
  delta: z.number().nullable().optional(),
  rd: nullableNumber,
  setId: z.string().nullable().default(null),
  /** The set's mean difficulty, 0–100 (G2). */
  setMean: nullableNumber,
  score: nullableNumber,
  played: count.nullable().default(null),
  startedAt: z.string().nullable().default(null),
  finishedAt: z.string().nullable().default(null),
  forfeit: z.boolean().default(false),
  puzzles: z.array(DuelPuzzle).default([]),
  // Phase 4 follow-ups (GET /duels/:id): RD before and after, the current rating with its tier, the set's
  // spread, progress, points and total solving time.
  rdBefore: nullableNumber,
  rdAfter: nullableNumber,
  ratingNow: nullableNumber,
  tier: z.string().nullable().default(null),
  division: z.string().nullable().default(null),
  moderationHold: z.boolean().default(false),
  setSpread: nullableNumber,
  started: z.boolean().nullable().default(null),
  finished: z.boolean().nullable().default(null),
  points: nullableNumber,
  timeMs: nullableNumber,
});
const toSide = ({ ratingBefore, delta: deltaAlias, finished, ...s }: z.output<typeof DuelSideIn>) => {
  const before = s.rating ?? ratingBefore ?? null;
  const delta = s.ratingDelta ?? deltaAlias ?? (before !== null && s.ratingAfter !== null ? s.ratingAfter - before : null);
  return {
    ...s,
    rating: before,
    ratingDelta: delta,
    ratingAfter: s.ratingAfter ?? (before !== null && delta !== null ? before + delta : null),
    rd: s.rd ?? s.rdAfter ?? s.rdBefore,
    finished: finished ?? s.finishedAt !== null,
    // `n` is the 1-based position in set order (backend/api's `index` is 0-based).
    puzzles: s.puzzles.map((p, i) => ({ ...p, index: p.index ?? i, n: i + 1 })),
  };
};
export type DuelSide = ReturnType<typeof toSide>;

const DuelRowIn = z.object({
  id,
  mode: z.string().default("friend"),
  status: z.string().default("pending"),
  rated: z.boolean().optional(),
  createdAt: z.string().nullable().default(null),
  acceptedAt: z.string().nullable().default(null),
  windowEndsAt: z.string().nullable().default(null),
  settledAt: z.string().nullable().default(null),
  challenger: DuelSideIn.optional(),
  opponent: DuelSideIn.nullable().optional(),
  a: DuelSideIn.optional(),
  b: DuelSideIn.nullable().optional(),
  sides: z.array(DuelSideIn).optional(),
  /** Difficulty gap between the two sets' means (G2), 0–100. */
  gap: nullableNumber,
  /** "challenger" | "opponent" | "a" | "b" | "draw" | "void" | a userId | `{ userId }`; backend/api calls it `outcome`. */
  winner: z.union([z.string(), z.object({ userId: z.string().nullable().default(null) })]).nullable().default(null),
  outcome: z.string().nullable().optional(),
  /** backend/api: which side forfeited ("challenger" | "opponent"). */
  forfeit: z.string().nullable().optional(),
  type: z.string().nullable().default(null),
  seriesId: z.string().nullable().default(null),
  game: count.nullable().default(null),
  seriesGame: count.nullable().optional(),
  // Phase 4 follow-ups (GET /duels/:id).
  link: z.string().nullable().default(null),
  week: z.string().nullable().default(null),
  matchedBy: z.string().nullable().default(null),
  chooser: z.string().nullable().default(null),
  declinedBy: z.array(z.string()).nullish().transform((v) => v ?? []),
  expiresAt: z.string().nullable().default(null),
  updatedAt: z.string().nullable().default(null),
});

/**
 * A duel: `{ id, mode, status, rated, createdAt, acceptedAt, windowEndsAt, settledAt, challenger, opponent,
 * gap, winner }` with sides `{ userId, name, handle, orb, rating, ratingAfter, ratingDelta, rd, setId,
 * setMean, score, played, startedAt, finishedAt, forfeit, puzzles? }`. `a`/`b` or `sides: [a, b]` are
 * accepted for the sides, `ratingBefore` for `rating`; status aliases (accepted, finished, …) map onto
 * pending / active / settled / declined / expired. Missing gaps and deltas are derived.
 */
export const DuelRow = DuelRowIn.transform((d) => {
  const challengerIn = d.challenger ?? d.a ?? d.sides?.[0];
  const opponentIn = d.opponent ?? d.b ?? d.sides?.[1] ?? null;
  const challenger = toSide(challengerIn ?? DuelSideIn.parse({}));
  const opponent = opponentIn ? toSide(opponentIn) : null;
  if (d.forfeit === "challenger") challenger.forfeit = true;
  if (d.forfeit === "opponent" && opponent) opponent.forfeit = true;
  const mode = DuelMode.safeParse(d.mode).success ? (d.mode as DuelMode) : ("friend" as const);
  const w = d.winner ?? d.outcome ?? null;
  const wid = typeof w === "string" ? w : (w?.userId ?? null);
  const winner: "challenger" | "opponent" | "draw" | "void" | null =
    wid === null
      ? null
      : wid === "void"
        ? "void"
        : wid === "draw" || wid === "tie"
        ? "draw"
        : wid === "challenger" || wid === "a" || wid === challenger.userId
          ? "challenger"
          : wid === "opponent" || wid === "b" || (opponent && wid === opponent.userId)
            ? "opponent"
            : null;
  return {
    id: d.id,
    mode,
    status: DUEL_STATUS_ALIASES[d.status.toLowerCase()] ?? ("pending" as DuelStatus),
    type: d.type,
    rated: d.rated ?? mode !== "friend",
    createdAt: d.createdAt,
    acceptedAt: d.acceptedAt,
    windowEndsAt: d.windowEndsAt,
    settledAt: d.settledAt,
    challenger,
    opponent,
    gap: d.gap ?? (challenger.setMean !== null && opponent?.setMean != null ? Math.round(Math.abs(challenger.setMean - opponent.setMean) * 10) / 10 : null),
    winner,
    seriesId: d.seriesId,
    game: d.game ?? d.seriesGame ?? null,
    link: d.link,
    week: d.week,
    matchedBy: d.matchedBy,
    chooser: d.chooser,
    declinedBy: d.declinedBy,
    expiresAt: d.expiresAt,
    updatedAt: d.updatedAt,
  };
});
export type DuelRow = z.output<typeof DuelRow>;

/** `{ items, nextCursor }`, `{ duels }` or a bare array. */
export const DuelList = z.union([
  z.object({ items: z.array(DuelRow), nextCursor: z.string().nullable().default(null) }),
  z.object({ duels: z.array(DuelRow), nextCursor: z.string().nullable().default(null) }).transform(({ duels, ...r }) => ({ ...r, items: duels })),
  z.array(DuelRow).transform((items) => ({ items, nextCursor: null as string | null })),
]);
export type DuelList = z.output<typeof DuelList>;

/** A duel's status history (Phase 4 follow-ups); `derived` rows were rebuilt from timestamps by migration 0008. */
export const DuelHistoryEntry = z.object({
  from: z.string().nullable().default(null),
  status: z.string(),
  at: z.string(),
  opponentId: z.string().nullable().default(null),
  derived: z.boolean().default(false),
});
export type DuelHistoryEntry = z.output<typeof DuelHistoryEntry>;

export const DUEL_FLAG_KINDS = ["too_fast", "clock_mismatch", "moderation_hold", "forfeit", "unfinished", "void", "overdue"] as const;
export const DuelFlag = z.object({
  kind: z.string(),
  side: z.enum(["challenger", "opponent"]).nullable().default(null),
  index: count.nullable().default(null),
});
export type DuelFlag = z.output<typeof DuelFlag>;

export const DuelSeries = z.object({
  id: z.string(),
  status: z.string(),
  week: z.string().nullable().default(null),
  deadline: z.string().nullable().default(null),
  wins: z.object({ challenger: count, opponent: count }),
  draws: count.default(0),
  outcome: z.string().nullable().default(null),
  createdAt: z.string().nullable().default(null),
  settledAt: z.string().nullable().default(null),
  games: z
    .array(
      z.object({
        game: count,
        duelId: z.string(),
        status: z.string(),
        type: z.string().nullable().default(null),
        outcome: z.string().nullable().default(null),
        chooser: z.string().nullable().default(null),
      }),
    )
    .default([]),
});
export type DuelSeries = z.output<typeof DuelSeries>;

/**
 * `GET /duels/:id` (Phase 4 follow-ups): `{ duel, series, history, flags }`. `{ duel }` alone or the bare row
 * (an older API, or the list fallback) read as a detail with no series, history or flags; `full` says
 * whether the API sent the detail route's shape.
 */
export const DuelDetail = z.union([
  z
    .object({ duel: DuelRow, series: DuelSeries.nullish(), history: z.array(DuelHistoryEntry).optional(), flags: z.array(DuelFlag).optional() })
    .transform((b) => ({ duel: b.duel, series: b.series ?? null, history: b.history ?? [], flags: b.flags ?? [], full: b.history !== undefined })),
  DuelRow.transform((duel) => ({ duel, series: null as DuelSeries | null, history: [] as DuelHistoryEntry[], flags: [] as DuelFlag[], full: false })),
]);
export type DuelDetail = z.output<typeof DuelDetail>;

// Rooms (contracts/phase4.md §C).

export const RoomMember = z
  .object({
    userId: z.string().nullable().default(null),
    name: z.string().nullable().default(null),
    handle: z.string().nullable().default(null),
    orb: orbRef,
    role: z.string().nullable().default(null),
    joinedAt: z.string().nullable().default(null),
    score: nullableNumber,
    played: count.nullable().default(null),
    finished: z.boolean().nullable().default(null),
    finishedAt: z.string().nullable().default(null),
    // Phase 4 follow-ups (GET /rooms/:code): the board as admins see it.
    state: z.string().nullable().default(null),
    invitedAt: z.string().nullable().default(null),
    started: z.boolean().nullable().default(null),
    /** The board's rank as players see it (finished sets; every started set once the room closed). */
    rank: count.nullable().default(null),
    /** Total server-measured solving time. */
    timeMs: nullableNumber,
    startedAt: z.string().nullable().default(null),
    flags: z.array(z.string()).nullish().transform((v) => v ?? []),
  })
  .transform((m) => ({ ...m, finished: m.finished ?? m.finishedAt !== null }));
export type RoomMember = z.output<typeof RoomMember>;

const RoomRowIn = z.object({
  id,
  code: z.string(),
  type: z.string().default("mixed"),
  window: z.union([z.string(), z.number()]).nullable().default(null),
  status: z.string().nullable().default(null),
  createdAt: z.string().nullable().default(null),
  closesAt: z.string().nullable().default(null),
  creator: z
    .object({ userId: z.string().nullable().default(null), name: z.string().nullable().default(null), handle: z.string().nullable().default(null), orb: orbRef })
    .nullable()
    .default(null),
  creatorId: z.string().nullable().optional(),
  // Phase 4 follow-ups (GET /rooms/:code).
  url: z.string().nullable().default(null),
  week: z.string().nullable().default(null),
  members: z.union([count, z.array(RoomMember)]).optional(),
  memberCount: count.optional(),
  /** Invited but not joined yet (backend/api). */
  invited: count.nullable().default(null),
  closedAt: z.string().nullable().default(null),
  cap: count.default(8),
  finished: count.optional(),
  setId: z.string().nullable().default(null),
  rematchOf: z.string().nullable().default(null),
  rematchId: z.string().nullable().default(null),
});
const toRoom = ({ members, memberCount, finished, creatorId, window, status, ...r }: z.output<typeof RoomRowIn>) => {
  const list = Array.isArray(members) ? members : [];
  const w = String(window ?? "").toLowerCase();
  const closed = status === "closed" || (status !== "open" && r.closesAt !== null && Date.parse(r.closesAt) <= Date.now());
  return {
    ...r,
    window: w === "24h" || w === "24" ? ("24h" as const) : w === "week" || w === "168" || w === "168h" || w === "7d" ? ("week" as const) : null,
    status: closed ? ("closed" as const) : ("open" as const),
    creator: r.creator ?? (creatorId ? { userId: creatorId, name: null, handle: null, orb: null } : null),
    memberCount: memberCount ?? (typeof members === "number" ? members : list.length),
    finished: finished ?? (list.length ? list.filter((m) => m.finished).length : null),
    members: list,
  };
};

/**
 * A room: `{ id, code, type, window, status, createdAt, closesAt, creator: { userId, name, handle },
 * members: n | [member], cap, finished, setId, rematchOf }`. `creatorId` and `memberCount` are accepted;
 * the status is derived from `closesAt` when absent. Never rated.
 */
export const RoomRow = RoomRowIn.transform(toRoom);
export type RoomRow = z.output<typeof RoomRow>;

export const RoomList = z.union([
  z.object({ items: z.array(RoomRow), nextCursor: z.string().nullable().default(null) }),
  z.object({ rooms: z.array(RoomRow), nextCursor: z.string().nullable().default(null) }).transform(({ rooms, ...r }) => ({ ...r, items: rooms })),
  z.array(RoomRow).transform((items) => ({ items, nextCursor: null as string | null })),
]);
export type RoomList = z.output<typeof RoomList>;

/**
 * `GET /rooms/:code` (Phase 4 follow-ups; a code or a room id): `{ room, members: [{ userId, name, handle,
 * orb, role, state, joinedAt, invitedAt, started, played, finished, score, rank, timeMs, startedAt,
 * finishedAt, flags }] }`, or the room with its members inline. The board: ranked members first (by
 * rank), then finished, then by progress and score.
 */
export const RoomDetail = z
  .union([z.object({ room: RoomRowIn, members: z.array(RoomMember).optional() }), RoomRowIn.transform((room) => ({ room, members: undefined }))])
  .transform(({ room, members }) => {
    const r = toRoom(room);
    const list = members ?? r.members;
    const sorted = [...list].sort(
      (a, b) =>
        (a.rank ?? Infinity) - (b.rank ?? Infinity) ||
        Number(b.finished) - Number(a.finished) ||
        (b.played ?? 0) - (a.played ?? 0) ||
        (b.score ?? -1) - (a.score ?? -1),
    );
    return { room: { ...r, members: sorted, memberCount: Math.max(r.memberCount, list.length), finished: r.finished ?? list.filter((m) => m.finished).length }, members: sorted };
  });
export type RoomDetail = z.output<typeof RoomDetail>;

export const TeamMutationResponse = z.unknown();

export const ProGrantResponse = z.unknown();
export const ReportPatchResponse = z.unknown();

export const Integrity = z.object({ date: isoDate, items: z.array(FlaggedSubmission) });

// ---------------------------------------------------------------- config

export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };
export const JsonValueSchema: z.ZodType<JsonValue> = z.lazy(() =>
  z.union([z.string(), z.number(), z.boolean(), z.null(), z.array(JsonValueSchema), z.record(z.string(), JsonValueSchema)]),
);

/** Every known key; keys not stored yet show their default. GET is open to every role. */
export const Config = z.object({ values: z.record(z.string(), JsonValueSchema) });
export type Config = z.infer<typeof Config>;
export const ConfigPutResponse = z.object({ key: z.string(), value: JsonValueSchema });
export const ConfigPatchResponse = z.object({ values: z.record(z.string(), JsonValueSchema) });

// ---------------------------------------------------------------- audit

export const AuditEntry = z.object({
  id,
  /** null: the system (a job), e.g. app.version.auto. */
  adminId: id.nullable(),
  adminEmail: z.string().nullable(),
  action: z.string(),
  target: z.string().nullable(),
  payload: z.unknown(),
  createdAt: z.string(),
});
export type AuditEntry = z.infer<typeof AuditEntry>;

export const AuditPage = z.object({ items: z.array(AuditEntry), nextCursor: z.string().nullable() });
export type AuditPage = z.infer<typeof AuditPage>;
